import React from 'react';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';

import { CREATE_APPOINTMENT } from 'pages/SchedulePage/gqlSchemes/createAppointment';
import { CREATE_EVENT } from 'pages/SchedulePage/gqlSchemes/createEvent';
import { actions as calendarActions } from 'services/calendar';
import { eachDayOfInterval, format, formatISO, parseISO } from 'date-fns';
import { CREATE_ABSENCE } from '../gqlSchemes/createAbsence';
import { CREATE_RANGE_APPOINTMENT } from '../gqlSchemes/createRangeAppointment';
import { GET_CALENDAR } from '../gqlSchemes/getCalendar';

function useCreateRequest(props) {
  const dispatch = useDispatch();

  const [
    createAppointment,
    {
      called,
      loading: loadingCreateAppointment,
      error: appointmentError,
      data: appointment,
    },
  ] = useMutation(CREATE_APPOINTMENT);

  const [
    createRangeAppointment,
    {
      called: calledRangeAppointment,
      loading: loadingCreateRangeAppointment,
      error: appointmentRangeError,
      data: appointmentRange,
    },
  ] = useMutation(CREATE_RANGE_APPOINTMENT);

  const [
    createEvent,
    {
      called: calledEvent,
      loading: loadingCreateEvent,
      error: eventError,
      data: event,
    },
  ] = useMutation(CREATE_EVENT);

  const [
    createAbsence,
    {
      called: calledAbsence,
      loading: loadingCreateAbsence,
      error: absenceError,
      data: absence,
    },
  ] = useMutation(CREATE_ABSENCE);

  const onCreateAppointment = async (payload) => {
    const dateIntervalISO = eachDayOfInterval({
      start: payload.record?.startDate,
      end: payload.record?.endDate,
    }).map((item) => format(item, 'yyyy-MM-dd'));

    try {
      payload.record.isRange
        ? await createRangeAppointment({
            refetchQueries: [GET_CALENDAR, 'GetCalendar'],
            variables: {
              record: {
                // title: payload?.record.title || "Appointment",
                title: 'Appointment',
                eventType: payload?.record.eventType,
                patient: payload?.record.patient,
                users: payload?.record.users,
                isOnline: payload.record.isOnline,
                note: payload?.record.note,
                planifiedDuration: payload.record.planifiedDuration,
                possibleDateRange: dateIntervalISO,
                locationId: payload?.record.locationId,
                roomId: payload?.record?.roomId,
                reservationModeId: payload?.record?.reservationModeId,
                files: payload?.record.files,
              },
            },
          })
        : await createAppointment({
            refetchQueries: [GET_CALENDAR, 'GetCalendar'],
            variables: {
              record: {
                title: 'Appointment',
                users: payload?.record.users,
                startDate: payload?.record.startDate.toISOString(),
                eventType: payload?.record.eventType,
                isOnline: payload.record.isOnline,
                note: payload?.record.note,
                patient: payload.record.patient,
                planifiedDuration: payload.record.planifiedDuration,
                locationId: payload?.record.locationId,
                roomId: payload?.record?.roomId,
                reservationModeId: payload?.record?.reservationModeId,
                files: payload?.record.files,
              },
            },
          });
    } catch (e) {
      alert(e);
    }
  };

  const onCreateEvent = async (payload) => {
    const response = await createEvent({
      variables: {
        record: {
          endDate: parseISO(payload.endDate),
          startDate: parseISO(payload.startDate),
          title: payload.title,
          users: payload.users,
          isOnline: false,
        },
      },
    });

    if (response && !response.data.schedule.createEvent.ok) {
      const { message } = response.data.schedule.createEvent.error;
      dispatch(calendarActions.setEventError({ error: message }));
    }

    if (response && response.data.schedule.createEvent.ok) {
      dispatch(
        calendarActions.addEvent({
          event: {
            ...response.data.schedule.createEvent.result,
            startDate: response.data.schedule.createEvent.result?.startDate?.split(
              '+'
            )[0],
            endDate: response.data.schedule.createEvent.result?.endDate?.split(
              '+'
            )[0],
          },
        })
      );
    }
  };

  const onCreateAbsence = async (payload) => {
    const response = await createAbsence({
      refetchQueries: [GET_CALENDAR, 'GetCalendar'],
      variables: {
        record: {
          ...payload,
        },
      },
      onError: (err) => {}, // error handling if status 400
      onCompleted: (res) => {
        if (!res.user.dashboardUser.createPractitionerAbsence.ok) {
          alert(res.user.dashboardUser.createPractitionerAbsence.error.message);
        }
      },
    });

    return response.data.user.dashboardUser.createPractitionerAbsence.ok;
  };

  return {
    onCreateAppointment,
    onCreateEvent,
    onCreateAbsence,
    loadingCreateAppointment,
    loadingCreateEvent,
    loadingCreateAbsence,
  };
}

export default useCreateRequest;
